import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, Film, ImagePlus, Megaphone, Pencil, Plus, Trash2 } from 'lucide-react';
import { StoryMedia, UPLOAD } from '@kick/types';
import { api, getErrorDetails, getErrorMessage } from '@/lib/api';
import { usePuede } from '@/lib/permisos';
import { cn } from '@/lib/cn';
import { useComerciosAdmin } from '@/lib/queries';
import {
  Boton,
  Campo,
  Cargando,
  ConfirmarModal,
  Entrada,
  ErrorCarga,
  Modal,
  Vacio,
} from '@/components/ui';
import { SelectorBuscable } from '@/components/SelectorBuscable';
import { useAvisos } from '@/components/avisos';

interface Anuncio {
  id: string;
  mediaUrl: string;
  mediaType: StoryMedia;
  posterUrl: string | null;
  caption: string | null;
  durationSeconds: number;
  publishedAt: string;
  expiresAt: string;
  retiredAt: string | null;
  views: number;
  /** Nulos en un anuncio general: no lleva a ningún comercio. */
  merchantId: string | null;
  merchantName: string | null;
}

/**
 * Anuncios de la portada.
 *
 * Duran veinticuatro horas y se apagan solos. La pantalla muestra también los
 * vencidos porque la pregunta que se hace quien entra no es solo «qué está
 * saliendo» sino «qué publiqué ayer y cuántos lo vieron».
 */
export function Anuncios() {
  const puede = usePuede();
  const cliente = useQueryClient();
  const avisos = useAvisos();
  const [publicando, setPublicando] = useState(false);
  const [retirando, setRetirando] = useState<Anuncio | null>(null);
  const [eliminando, setEliminando] = useState<Anuncio | null>(null);
  const [editando, setEditando] = useState<Anuncio | null>(null);

  const lista = useQuery({
    queryKey: ['anuncios-admin'],
    queryFn: async () => (await api.get<Anuncio[]>('/anuncios?limit=100')).data,
  });

  const retirar = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/anuncios/${id}`)).data,
    onSuccess: () => {
      avisos.exito('Anuncio retirado');
      setRetirando(null);
      void cliente.invalidateQueries({ queryKey: ['anuncios-admin'] });
    },
    onError: (e) => avisos.error(getErrorMessage(e)),
  });

  const eliminar = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/anuncios/${id}/definitivo`)).data,
    onSuccess: () => {
      avisos.exito('Anuncio eliminado');
      setEliminando(null);
      void cliente.invalidateQueries({ queryKey: ['anuncios-admin'] });
    },
    onError: (e) => avisos.error(getErrorMessage(e)),
  });

  if (lista.isLoading) return <Cargando />;
  if (lista.isError) {
    return (
      <ErrorCarga mensaje={getErrorMessage(lista.error)} onReintentar={() => void lista.refetch()} />
    );
  }

  const anuncios = lista.data ?? [];
  const vivos = anuncios.filter((a) => !a.retiredAt && new Date(a.expiresAt) > new Date());

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">Anuncios</h1>
          <p className="mt-1 text-sm text-kick-gray">
            Salen en la portada de la tienda como estados y duran 24 horas.
            {vivos.length > 0 && ` Ahora hay ${vivos.length} al aire.`}
          </p>
        </div>
        {puede('anuncios', 'crear') && (
          <Boton icono={<Plus className="h-4 w-4" />} onClick={() => setPublicando(true)}>
            Publicar anuncio
          </Boton>
        )}
      </div>

      {anuncios.length === 0 ? (
        <div className="mt-6">
          <Vacio
            icono={<Megaphone className="h-6 w-6" />}
            titulo="Todavía no hay anuncios"
            descripcion="Publica uno y aparece de inmediato en la portada de la tienda."
          />
        </div>
      ) : (
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {anuncios.map((a) => (
            <Tarjeta
              key={a.id}
              anuncio={a}
              onRetirar={() => setRetirando(a)}
              onEliminar={() => setEliminando(a)}
              onEditar={() => setEditando(a)}
            />
          ))}
        </div>
      )}

      {(publicando || editando) && (
        <Formulario
          anuncio={editando}
          onCerrar={() => {
            setPublicando(false);
            setEditando(null);
          }}
          onGuardado={() => {
            setPublicando(false);
            setEditando(null);
            void cliente.invalidateQueries({ queryKey: ['anuncios-admin'] });
          }}
        />
      )}

      <ConfirmarModal
        abierto={!!retirando}
        titulo="Retirar el anuncio"
        mensaje="Deja de verse en la portada de inmediato. Queda el registro de que se publicó y de cuántos lo vieron."
        textoConfirmar="Retirar"
        cargando={retirar.isPending}
        onConfirmar={() => retirando && retirar.mutate(retirando.id)}
        onCancelar={() => setRetirando(null)}
      />

      <ConfirmarModal
        abierto={!!eliminando}
        titulo="Eliminar el anuncio"
        mensaje="Se borra el archivo y el registro. No se puede deshacer, y se pierde el conteo de cuántos lo vieron."
        textoConfirmar="Eliminar"
        cargando={eliminar.isPending}
        onConfirmar={() => eliminando && eliminar.mutate(eliminando.id)}
        onCancelar={() => setEliminando(null)}
      />
    </div>
  );
}

function Tarjeta({
  anuncio: a,
  onRetirar,
  onEliminar,
  onEditar,
}: {
  anuncio: Anuncio;
  onRetirar: () => void;
  onEliminar: () => void;
  onEditar: () => void;
}) {
  const puede = usePuede();
  const vencido = new Date(a.expiresAt) <= new Date();
  const vivo = !a.retiredAt && !vencido;
  const horas = Math.max(0, Math.round((new Date(a.expiresAt).getTime() - Date.now()) / 3_600_000));

  return (
    <section className={cn('card-kick overflow-hidden p-0', !vivo && 'opacity-60')}>
      <div className="relative aspect-[9/16] max-h-64 bg-kick-dark">
        {a.mediaType === StoryMedia.VIDEO ? (
          <video
            src={a.mediaUrl}
            className="h-full w-full object-contain"
            muted
            playsInline
            // Solo la primera imagen: cargar el video entero para una miniatura
            // sería descargar veinte megas por tarjeta.
            preload="metadata"
          />
        ) : (
          <img src={a.mediaUrl} alt="" className="h-full w-full object-contain" />
        )}

        <span
          className={cn(
            'absolute left-2 top-2 rounded-full px-2 py-0.5 text-xs font-bold',
            vivo ? 'bg-emerald-500 text-white' : 'bg-black/60 text-white',
          )}
        >
          {a.retiredAt ? 'Retirado' : vencido ? 'Vencido' : `${horas} h`}
        </span>

        {a.mediaType === StoryMedia.VIDEO && (
          <span className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white">
            <Film className="h-3.5 w-3.5" />
          </span>
        )}
      </div>

      <div className="p-4">
        <p className={cn('truncate font-bold', a.merchantName ? 'text-kick-dark' : 'text-kick-gray')}>
          {a.merchantName ?? 'Sin comercio'}
        </p>
        {a.caption && <p className="mt-0.5 line-clamp-2 text-sm text-kick-gray">{a.caption}</p>}

        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-sm text-kick-gray">
            <Eye className="h-4 w-4" />
            {a.views}
          </span>

          {/*
            Un solo botón según el estado.
            Mientras está al aire lo que se quiere es bajarlo; una vez fuera,
            lo único que queda por hacer es quitarlo de la lista. Ofrecer los
            dos a la vez obliga a pensar cuál es cuál cada vez.
          */}
          {vivo ? (
            <span className="flex gap-2">
              {puede('anuncios', 'editar') && (
                <Boton
                  variante="secundario"
                  className="px-3 py-1.5 text-xs"
                  icono={<Pencil className="h-3.5 w-3.5" />}
                  onClick={onEditar}
                >
                  Editar
                </Boton>
              )}
              {puede('anuncios', 'eliminar') && (
                <Boton
                  variante="peligro"
                  className="px-3 py-1.5 text-xs"
                  icono={<EyeOff className="h-3.5 w-3.5" />}
                  onClick={onRetirar}
                >
                  Retirar
                </Boton>
              )}
            </span>
          ) : (
            puede('anuncios', 'eliminar') && (
              <Boton
                variante="secundario"
                className="px-3 py-1.5 text-xs"
                icono={<Trash2 className="h-3.5 w-3.5" />}
                onClick={onEliminar}
              >
                Eliminar
              </Boton>
            )
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Sirve para publicar y para corregir.
 *
 * Es el mismo formulario porque son los mismos campos; separarlos obligaría a
 * mantener dos copias que se van diferenciando sin que nadie lo note. Editando,
 * el archivo pasa a ser opcional: casi siempre lo que se corrige es el texto o
 * los segundos, no la pieza.
 */
function Formulario({
  anuncio,
  onCerrar,
  onGuardado,
}: {
  anuncio: Anuncio | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const editando = !!anuncio;
  const avisos = useAvisos();
  const comercios = useComerciosAdmin({ status: 'ACTIVE' });
  const entrada = useRef<HTMLInputElement>(null);
  const [merchantId, setMerchantId] = useState(anuncio?.merchantId ?? '');
  const [caption, setCaption] = useState(anuncio?.caption ?? '');
  const [duracion, setDuracion] = useState(anuncio?.durationSeconds ?? 5);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [errores, setErrores] = useState<Record<string, string[]>>({});

  /**
   * El tipo se deduce del archivo, no se pregunta.
   *
   * Quien publica sabe si eligió un video o una foto; obligarle a repetirlo en
   * un desplegable solo abre la puerta a que no coincidan.
   *
   * Sin archivo nuevo se conserva el del anuncio: editar el texto no debería
   * cambiar de qué clase de pieza se trata.
   */
  const tipo: StoryMedia | null = !archivo
    ? (anuncio?.mediaType ?? null)
    : archivo.type.startsWith('video/')
      ? StoryMedia.VIDEO
      : archivo.type === 'image/gif'
        ? StoryMedia.ANIMATED
        : StoryMedia.IMAGE;

  // Lo que se ve: la pieza recién elegida, o la que ya tenía el anuncio.
  const previa = archivo ? URL.createObjectURL(archivo) : (anuncio?.mediaUrl ?? null);

  const publicar = useMutation({
    mutationFn: async () => {
      const cuerpo = new FormData();
      if (archivo) cuerpo.append('archivo', archivo);
      cuerpo.append('merchantId', merchantId);
      if (tipo) cuerpo.append('mediaType', tipo);
      cuerpo.append('caption', caption.trim());
      if (tipo !== StoryMedia.VIDEO) cuerpo.append('durationSeconds', String(duracion));

      return editando
        ? (await api.patch(`/anuncios/${anuncio.id}`, cuerpo)).data
        : (await api.post('/anuncios', cuerpo)).data;
    },
    onSuccess: () => {
      avisos.exito(
        editando
          ? 'Anuncio actualizado. Sigue venciendo a la misma hora.'
          : 'Anuncio publicado. Ya se ve en la portada.',
      );
      onGuardado();
    },
    onError: (e) => {
      const detalles = getErrorDetails(e);
      if (detalles) setErrores(detalles);
      else avisos.error(getErrorMessage(e));
    },
  });

  const topeMb = Math.round(
    (tipo === StoryMedia.VIDEO ? UPLOAD.maxVideoBytes : UPLOAD.maxImageBytes) / 1024 / 1024,
  );
  const demasiadoGrande =
    !!archivo &&
    archivo.size > (tipo === StoryMedia.VIDEO ? UPLOAD.maxVideoBytes : UPLOAD.maxImageBytes);

  return (
    <Modal abierto titulo={editando ? 'Corregir el anuncio' : 'Anuncio nuevo'} onCerrar={onCerrar}>
      <div className="space-y-4">
        <Campo
          etiqueta="Comercio"
          error={errores.merchantId?.[0]}
          ayuda="Opcional. Con comercio, tocar el anuncio abre su ficha; sin comercio, es solo la pieza y tocarlo no hace nada."
        >
          {/*
            Buscable y no un desplegable normal: con seis comercios da igual,
            pero esta lista solo va a crecer, y a partir de unas decenas
            recorrerla con la vista deja de funcionar.
          */}
          <SelectorBuscable
            valor={merchantId}
            onCambio={setMerchantId}
            placeholder="Sin comercio (anuncio general)"
            error={!!errores.merchantId}
            opciones={
              comercios.data?.data.map((c) => ({
                valor: c.id,
                texto: c.name,
                // La dirección desempata dos sucursales con el mismo nombre.
                detalle: c.addressLine,
              })) ?? []
            }
          />
          {merchantId && (
            <button
              type="button"
              onClick={() => setMerchantId('')}
              className="mt-1.5 text-xs font-semibold text-kick-red hover:underline"
            >
              Quitar el comercio
            </button>
          )}
        </Campo>

        <div>
          <p className="text-sm font-semibold text-kick-ink">
            Archivo {!editando && <span className="text-kick-red">*</span>}
          </p>
          <input
            ref={entrada}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm"
            aria-label="Archivo del anuncio"
            onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
            className="hidden"
          />

          <button
            type="button"
            onClick={() => entrada.current?.click()}
            className="mt-1.5 flex w-full items-center justify-center gap-2 rounded-kick border border-dashed border-kick-gray/40 px-4 py-6 text-sm text-kick-gray transition hover:border-kick-red hover:text-kick-red"
          >
            <ImagePlus className="h-5 w-5" />
            {archivo ? archivo.name : editando ? 'Cambiar el archivo' : 'Elegir imagen, GIF o video'}
          </button>

          <p className="mt-1 text-xs text-kick-gray">
            Imagen o GIF hasta {Math.round(UPLOAD.maxImageBytes / 1024 / 1024)} MB · video MP4 o
            WebM hasta {Math.round(UPLOAD.maxVideoBytes / 1024 / 1024)} MB. Sale en un banner
            ancho: una pieza apaisada —tipo 1600×600— se ve mejor, pero cualquier proporción
            funciona.
          </p>

          {demasiadoGrande && (
            <p className="mt-1 text-sm text-red-600">
              Este archivo pesa {Math.round(archivo.size / 1024 / 1024)} MB y el tope es {topeMb} MB.
            </p>
          )}
        </div>

        {previa && (
          <div className="overflow-hidden rounded-kick bg-kick-dark">
            {tipo === StoryMedia.VIDEO ? (
              <video src={previa} className="mx-auto max-h-64" controls muted playsInline />
            ) : (
              <img src={previa} alt="" className="mx-auto max-h-64" />
            )}
          </div>
        )}

        <Campo etiqueta="Texto" ayuda="Opcional. Sale sobre el anuncio, abajo.">
          <Entrada
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={140}
            placeholder="Hoy 2x1 en hamburguesas hasta las 8 p. m."
          />
        </Campo>

        {tipo !== StoryMedia.VIDEO && (
          <Campo etiqueta="Segundos en pantalla" ayuda="Entre 3 y 15. El video usa su duración.">
            <Entrada
              type="number"
              min={3}
              max={15}
              value={duracion}
              onChange={(e) => setDuracion(Number(e.target.value))}
            />
          </Campo>
        )}

        <p className="rounded-kick bg-kick-light px-3 py-2 text-sm text-kick-ink">
          {editando
            ? 'Corregirlo no reinicia el reloj: sigue venciendo a la misma hora que tenía.'
            : 'Se publica ahora y desaparece solo en 24 horas.'}
        </p>

        <div className="flex justify-end gap-2 pt-2">
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            onClick={() => publicar.mutate()}
            cargando={publicar.isPending}
            disabled={(!archivo && !editando) || demasiadoGrande}
          >
            {editando ? 'Guardar' : 'Publicar'}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}
